/**
 * commentEditorSeedHtml - the HTML the TipTap editor should open with
 * when an agent clicks Edit on an existing comment.
 *
 * Comments do not always carry ``body_html``. The public API accepts a
 * bare ``body_text`` (no ``body_markdown``, no ``body_html``), and that
 * is what the AI posts its internal notes as - see
 * ``PublicCommentCreate.resolved_body`` in backend/app/api/public_api.py,
 * which returns ``html = None`` on that branch. Every API internal note
 * in prod is stored that way.
 *
 * The read view already handles the null case by rendering ``body_text``
 * in a ``whitespace-pre-wrap`` block, but the editor was seeded from
 * ``body_html`` alone, so clicking Edit on one of those notes opened a
 * blank editor and saving would have wiped the note.
 *
 * So: fall back to ``body_text``, converted to the paragraph-per-line
 * shape TipTap expects. Text is escaped rather than linkified, which
 * keeps the editor's contents identical to what the collapsed bubble
 * showed a moment earlier - an Edit that silently rewrote the note's
 * markup would be its own bug.
 *
 * Paragraphs are emitted bare. ``TightParagraph`` in RichTextEditor.tsx
 * re-emits every ``<p>`` with its own inline style once the content is
 * parsed, so adding the style here would be redundant.
 */

/** Replace HTML-special characters with their entities. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function plainTextToEditorHtml(text: string): string {
  // CRLF and bare CR both show up in imported Freshdesk bodies; split on
  // any of the three line endings so an imported note does not open as
  // one giant paragraph.
  const normalized = text.replace(/\r\n?/g, "\n");
  // Drop a single trailing newline so the seed doesn't end on a dangling
  // empty paragraph the agent has to backspace away.
  const trimmed = normalized.endsWith("\n")
    ? normalized.slice(0, -1)
    : normalized;
  if (trimmed.length === 0) return "";
  return trimmed
    .split("\n")
    .map((line) =>
      line.length === 0 ? "<p></p>" : `<p>${escapeHtml(line)}</p>`,
    )
    .join("");
}

export function commentEditorSeedHtml(
  bodyHtml: string | null | undefined,
  bodyText: string | null | undefined,
): string {
  if (bodyHtml) return bodyHtml;
  return plainTextToEditorHtml(bodyText ?? "");
}
